/**
 * useSessionEventStream — one conversation's own event stream.
 *
 * GET /sessions/{id}/events on tricorder-session (SPEC.md section 7), reached
 * through the gateway proxy: turn.delta text as it is produced, turn.completed
 * naming the answer's id (the record itself is the catch-up read's — the frame
 * never carries it), turn.failed with the reason, turn.directives (Agentic UI
 * Fabric), turn.elicitation (a blocked tool call's question, its id, and the
 * requested schema). The stream replays nothing — a client that missed events
 * re-reads the messages after the last seq it saw (useSessions' messages
 * query). The server subscribes before it writes its `connected` frame, so on
 * that frame the session is re-read once: a turn that completed before the
 * stream opened is picked up then.
 *
 * ONE CONNECTION PER SESSION, shared by every mounted consumer (the Home pane,
 * the full-screen session page, a board's side-channel can all be bound to one
 * id): a module-level refcount opens the stream at 1 and closes it at 0, so
 * two views of one session never append one delta twice.
 *
 * ONLY WHILE THE PAGE IS VISIBLE. An open stream holds a gateway and a
 * tricorder-session request, and each one bills a whole vCPU on Cloud Run for
 * as long as it is open. Hiding the page closes the connection; showing it
 * again opens a new one, whose `connected` frame runs the same catch-up read
 * as every other reconnect, so a turn that finished while the page was hidden
 * appears then.
 */

import { useEffect } from 'react';
import type { QueryClient } from '@tanstack/react-query';
import { sessionKeys, type SessionDetail } from './useSessions';
import { getApiBaseUrl } from '@/api/baseUrl';
import { recoverFromUnauthorized } from '@/api/client';
import { useAuthStore } from '@/stores/auth';
import { pendingTurnsForSession, turnKey, usePendingTurnsStore } from '@/stores/pendingTurns';
import { sessionsApi } from '@/api/endpoints/sessions';
import { usePendingElicitationsStore } from '@/stores/pendingElicitations';
import { useFabricGuideStore } from '@/stores/fabricGuide';
import { errorToast } from '@/hooks/errorToast';
import { createLogger } from '@/utils/logger';
import i18n from '@/i18n';
import { conversationText, isTurnReply, turnFailurePayload } from '@/api/types_session';

/** The SSE frames of GET /sessions/{id}/events: `event:` names the type, the
 *  JSON body carries session_id, the turn's seq, and the payload. */
interface SSEEventFrame {
  type: string;
  session_id?: string;
  seq?: number;
  payload?: unknown;
}

const logger = createLogger('SessionEventStream');
const BASE_URL = getApiBaseUrl();
const RECONNECT_DELAY_MS = 1000;

interface SessionStream {
  refCount: number;
  close: () => void;
}

const streams = new Map<string, SessionStream>();

/** Append a delta chunk to the placeholder's text block. */
function appendDelta(queryClient: QueryClient, sessionId: string, placeholderId: string, delta: string): void {
  const cacheKey = sessionKeys.detail(sessionId);
  const detail = queryClient.getQueryData<SessionDetail>(cacheKey);
  if (!detail) return;
  const items = detail.messages.items.map((m) => {
    if (m.id !== placeholderId) return m;
    const text = conversationText(m.payload) + delta;
    return { ...m, payload: [{ type: 'text', text: { text } }] };
  });
  queryClient.setQueryData<SessionDetail>(cacheKey, {
    ...detail,
    messages: { ...detail.messages, items },
  });
}

/** turn.completed names the answer's id, nothing more — the message record is
 *  resolved upstream for its own listeners and never serialized into the
 *  frame. The client's copy of the record is the catch-up read (SPEC.md
 *  section 7): invalidate, and the refetched messages replace the placeholder
 *  with the durable record. */
function completeTurn(queryClient: QueryClient, sessionId: string): void {
  void queryClient.invalidateQueries({ queryKey: sessionKeys.detail(sessionId) });
  void queryClient.invalidateQueries({ queryKey: sessionKeys.lists() });
}

/** A terminal failure: drop the partial placeholder; the durable turn_failure
 * record arrives with the next messages read.
 *
 * The toast the user sees is also the report the team gets, so it carries the
 * keys only this site has: the session, the seq of the message that started
 * the turn, the failure KIND, and the pinned agent from the session cache. A
 * turn failure arrives on this stream, not as an ApiError — without those
 * keys the report names neither what ran nor where to read the server side. */
function failTurn(
  queryClient: QueryClient,
  sessionId: string,
  placeholderId: string,
  reason: string,
  seq: number,
  failure?: string,
): void {
  const cacheKey = sessionKeys.detail(sessionId);
  const detail = queryClient.getQueryData<SessionDetail>(cacheKey);
  if (detail) {
    const items = detail.messages.items.filter((m) => m.id !== placeholderId);
    queryClient.setQueryData<SessionDetail>(cacheKey, {
      ...detail,
      messages: { ...detail.messages, items },
    });
  }
  void queryClient.invalidateQueries({ queryKey: sessionKeys.detail(sessionId) });
  void queryClient.invalidateQueries({ queryKey: sessionKeys.lists() });
  errorToast({
    context: 'turnFailed',
    description: reason,
    // The kind only when the payload was readable: 'unreadable' names a
    // condition of the CLIENT's parse, not a kind the platform chose, so it
    // is not put on the wire as error_code. An absent code says "unknown",
    // which is the truth.
    errorCode: failure,
    sessionId,
    turnSeq: seq,
    agentSlug: detail?.session.agent?.slug,
    agentVersion: detail?.session.agent?.version,
  });
}

/**
 * catchUp runs once per connection, on its `connected` frame. The server
 * subscribes before it writes that frame, so whatever finished before the
 * stream opened is in the messages now, and nothing after it is missed.
 *
 * Every query under the session's detail key is re-read, except while a turn
 * is pending: the detail cache then holds the client-only placeholder the
 * deltas stream into, which a refetch would delete. So the messages after the
 * oldest pending turn, every page of them, decide which turns already ended;
 * those are cleared, and the detail itself is re-read only once no turn is
 * left pending.
 *
 * A pending turn with no seq (one persisted before turns carried it) cannot be
 * matched to its reply, so it is dropped here and the plain detail re-read
 * settles it: the re-read shows the reply if it landed, and nothing pending
 * if it did not.
 */
async function catchUp(queryClient: QueryClient, sessionId: string): Promise<void> {
  const detailKey = sessionKeys.detail(sessionId);
  const store = usePendingTurnsStore.getState();
  const pending: { turnId: string; seq: number }[] = [];
  for (const t of pendingTurnsForSession(store.turns, sessionId)) {
    if (t.seq === undefined) store.clearTurn(t.turnId);
    else pending.push({ turnId: t.turnId, seq: t.seq });
  }

  if (pending.length > 0) {
    const items = await sessionsApi.messagesAfter(sessionId, Math.min(...pending.map((t) => t.seq)));
    for (const t of pending) {
      if (items.some((m) => m.seq > t.seq && isTurnReply(m))) {
        usePendingTurnsStore.getState().clearTurn(t.turnId);
      }
    }
  }

  const stillPending =
    pendingTurnsForSession(usePendingTurnsStore.getState().turns, sessionId).length > 0;
  await queryClient.invalidateQueries({
    queryKey: detailKey,
    predicate: (q) => !stillPending || q.queryKey.length > detailKey.length,
  });
}

function clearPending(seq: number, sessionId: string): void {
  usePendingTurnsStore.getState().clearTurn(turnKey(sessionId, seq));
}

function dispatch(queryClient: QueryClient, sessionId: string, frame: SSEEventFrame): void {
  const seq = frame.seq ?? 0;
  const placeholderId = `pending-${sessionId}-${seq}`;
  switch (frame.type) {
    case 'turn.delta': {
      // PublishTurnDelta: payload is {delta}.
      const delta = (frame.payload as { delta?: string } | null)?.delta ?? '';
      if (delta) appendDelta(queryClient, sessionId, placeholderId, delta);
      return;
    }
    case 'turn.completed': {
      // PublishTurnCompleted: payload is {message_id}; the record itself is
      // the catch-up read's, not the frame's.
      completeTurn(queryClient, sessionId);
      clearPending(seq, sessionId);
      return;
    }
    case 'turn.failed': {
      // PublishTurnFailed: the payload is the turn_failure record itself
      // (tricorder-protos schemas/session `#TurnFailure`); its options arrive
      // with the durable record the catch-up read lists.
      const payload = turnFailurePayload(frame.payload);
      const reason = payload?.reason ?? i18n.t('chat.errors.unreadableFailure');
      failTurn(queryClient, sessionId, placeholderId, reason, seq, payload?.failure);
      clearPending(seq, sessionId);
      return;
    }
    case 'turn.directives': {
      const directives = Array.isArray(frame.payload) ? frame.payload : [frame.payload];
      for (const directive of directives) {
        useFabricGuideStore.getState().receive(sessionId, directive as never);
      }
      return;
    }
    case 'turn.elicitation': {
      // The frame's payload (pgbus.elicitationPayload): the id, the question,
      // and the requested schema, built from the row the notification named.
      const payload = frame.payload as {
        elicitation_id?: string;
        message?: string;
        requested_schema?: unknown;
      } | null;
      if (payload?.elicitation_id) {
        usePendingElicitationsStore.getState().open({
          elicitationId: payload.elicitation_id,
          message: payload.message ?? '',
          requestedSchemaJson: JSON.stringify(payload.requested_schema ?? null),
          sessionId,
        });
      }
      return;
    }
    case 'connected':
      // Subscribe, then read: once per connection (a reconnect is a new
      // connection), driven by this frame, never by a timer.
      void catchUp(queryClient, sessionId).catch((err: unknown) => {
        logger.warn('session catch-up read failed', {
          sessionId,
          error: err instanceof Error ? err.message : 'unknown',
        });
      });
      return;
    default:
      logger.debug('unhandled session event type', { type: frame.type });
  }
}

/**
 * The server refused the stream with a 4xx: the same request will be refused
 * again, so the stream does not reconnect on its own clock (WHATWG HTML fails
 * an EventSource on any non-200 answer for the same reason). A 401 first goes
 * through the token refresh every other request uses.
 */
class StreamRefused extends Error {
  constructor(readonly status: number) {
    super(`session events answered ${status}`);
  }
}

async function runStream(
  queryClient: QueryClient,
  sessionId: string,
  signal: AbortSignal,
  onConnected: () => void,
): Promise<void> {
  const token = useAuthStore.getState().accessToken;
  const response = await fetch(`${BASE_URL}/sessions/${sessionId}/events`, {
    headers: { Authorization: `Bearer ${token}` },
    signal,
  });
  if (response.status >= 400 && response.status < 500) {
    throw new StreamRefused(response.status);
  }
  if (!response.ok || !response.body) {
    throw new Error(`session events answered ${response.status}`);
  }
  onConnected();
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let eventType = '';
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop() ?? '';
    for (const line of lines) {
      if (line.startsWith('event:')) {
        eventType = line.slice('event:'.length).trim();
        continue;
      }
      if (!line.startsWith('data:')) continue;
      const data = line.slice('data:'.length).trim();
      if (!data) continue;
      let frame: SSEEventFrame;
      try {
        frame = { type: eventType, ...JSON.parse(data) } as SSEEventFrame;
      } catch {
        logger.warn('dropping malformed session event', { eventType, data: data.slice(0, 80) });
        continue;
      }
      if (!frame.type) frame.type = eventType;
      try {
        dispatch(queryClient, sessionId, frame);
      } catch (err) {
        logger.warn('session event handler failed', {
          type: frame.type,
          error: err instanceof Error ? err.message : 'unknown',
        });
      }
      eventType = '';
    }
  }
  // The server ended the stream: a drop like any other, reconnected the same way.
  throw new Error('session event stream ended');
}

/**
 * Hold the session's stream while the page is visible. Hiding the page aborts
 * the connection; showing it opens a new one. Returns the function that closes
 * it for good.
 */
function openStream(queryClient: QueryClient, sessionId: string): () => void {
  let abort = new AbortController();
  const start = () => {
    abort = new AbortController();
    const { signal } = abort;
    // A 401 earns one token refresh per established connection: a 401 on the
    // refreshed token means the server refuses this user, not an old token.
    let refreshed = false;
    const connect = () => {
      if (signal.aborted) return;
      const onConnected = () => {
        refreshed = false;
      };
      void runStream(queryClient, sessionId, signal, onConnected).catch(async (err: unknown) => {
        if (signal.aborted) return;
        if (err instanceof StreamRefused) {
          if (err.status === 401 && !refreshed) {
            refreshed = true;
            if ((await recoverFromUnauthorized()) && !signal.aborted) connect();
            return;
          }
          logger.warn('session event stream refused; not reconnecting', {
            sessionId,
            status: err.status,
          });
          return;
        }
        logger.debug('session event stream dropped; reconnecting', {
          sessionId,
          error: err instanceof Error ? err.message : 'unknown',
        });
        setTimeout(connect, RECONNECT_DELAY_MS + Math.random() * 500);
      });
    };
    connect();
  };
  const onVisibilityChange = () => {
    if (document.visibilityState === 'hidden') abort.abort();
    else start();
  };
  document.addEventListener('visibilitychange', onVisibilityChange);
  if (document.visibilityState === 'visible') start();
  return () => {
    document.removeEventListener('visibilitychange', onVisibilityChange);
    abort.abort();
  };
}

/**
 * Bind this component to the conversation's event stream. Mount/unmount only;
 * everything the stream produces lands in the query cache and the stores.
 */
export function useSessionEventStream(sessionId: string, queryClient: QueryClient): void {
  useEffect(() => {
    if (!sessionId) return;

    const existing = streams.get(sessionId);
    if (existing) {
      existing.refCount += 1;
    } else {
      streams.set(sessionId, { refCount: 1, close: openStream(queryClient, sessionId) });
    }

    return () => {
      const entry = streams.get(sessionId);
      if (!entry) return;
      entry.refCount -= 1;
      if (entry.refCount <= 0) {
        entry.close();
        streams.delete(sessionId);
      }
    };
  }, [sessionId, queryClient]);
}
